import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Certificados.css';

const Certificados = () => {
  const { user } = useAuth();
  const [certificado, setCertificado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmitirForm, setShowEmitirForm] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [formData, setFormData] = useState({
    id_usuario: null, // Para admin emitir para outros usuários
    validade_anos: 1,
    algoritmo: 'RSA',
    tamanho_chave: 2048
  });
  const [qrcode, setQrcode] = useState(null);
  const [solicitacaoPendente, setSolicitacaoPendente] = useState(null);
  const isAdmin = user?.tipo_usuario === 'admin';

  useEffect(() => {
    carregarCertificado();
    if (!isAdmin) {
      verificarSolicitacao();
    }
  }, []);
  
  const verificarSolicitacao = async () => {
    try {
      const response = await api.get('/certificados/minha-solicitacao');
      if (response.data.success && response.data.data) {
        setSolicitacaoPendente(response.data.data);
      }
    } catch (err) {
      // Se não houver solicitação, não fazer nada
      if (err.response?.status !== 404) {
        // Ignorar outros erros
      }
    }
  };

  const carregarCertificado = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/certificados/meu-certificado');
      if (response.data.success) {
        setCertificado(response.data.data.certificado);
      } else {
        setCertificado(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setCertificado(null);
      } else {
        setError(err.response?.data?.message || 'Erro ao carregar certificado');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmitir = async (e) => {
    e.preventDefault();
    try {
      setEmitindo(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/certificados/emitir', formData);
      
      if (response.data.success) {
        setSuccess('Certificado emitido com sucesso!');
        setShowEmitirForm(false);
        await carregarCertificado();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao emitir certificado');
    } finally {
      setEmitindo(false);
    }
  };

  const handleSolicitar = async (e) => {
    e.preventDefault();
    try {
      setEmitindo(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/certificados/solicitar', formData);
      
      if (response.data.success) {
        setSuccess('Solicitação de certificado enviada com sucesso! Aguarde aprovação do administrador.');
        setShowEmitirForm(false);
        await verificarSolicitacao();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao solicitar certificado');
    } finally {
      setEmitindo(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/certificados/download/${certificado.uuid}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado-${certificado.numero_serie}.pem`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Certificado baixado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao fazer download');
    }
  };

  const handleGerarQRCode = async () => {
    try {
      setError('');
      const response = await api.get(`/certificados/qrcode/${certificado.uuid}`);
      if (response.data.success) {
        setQrcode(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao gerar QR Code');
    }
  };

  const handleRevogar = async () => {
    if (!isAdmin) {
      setError('Apenas administradores podem revogar certificados');
      return;
    }
    
    if (!window.confirm('Tem certeza que deseja revogar este certificado? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setError('');
      const response = await api.post('/certificados/revogar', {
        id_certificado: certificado.id_certificado,
        motivo: 'administrativo',
        motivo_detalhado: 'Revogação administrativa'
      });
      
      if (response.data.success) {
        setSuccess('Certificado revogado com sucesso');
        await carregarCertificado();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao revogar certificado');
    }
  };

  const handleRenovar = async () => {
    if (!isAdmin) {
      setError('Apenas administradores podem renovar certificados');
      return;
    }
    
    try {
      setEmitindo(true);
      setError('');
      const response = await api.post('/certificados/renovar', {
        id_certificado: certificado.id_certificado,
        validade_anos: formData.validade_anos
      });
      
      if (response.data.success) {
        setSuccess('Certificado renovado com sucesso!');
        await carregarCertificado();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao renovar certificado');
    } finally {
      setEmitindo(false);
    }
  };

  if (loading) {
    return (
      <div className="certificados-container">
        <div className="certificados-loading">
          <p>Carregando informações do certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="certificados-container">
      <div className="certificados-header">
        <h1>Meu Certificado Digital</h1>
        <p>Gerencie seu certificado digital do DocLine</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {!certificado ? (
        <div className="certificado-card">
          <div className="certificado-empty">
            <div className="empty-icon">🔐</div>
            <h2>Você ainda não possui um certificado digital</h2>
            {solicitacaoPendente && !isAdmin ? (
              <div style={{ 
                padding: '1rem', 
                background: '#FEF3C7', 
                border: '1px solid #FCD34D', 
                borderRadius: 0,
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, color: '#92400E' }}>
                  ⏳ <strong>Solicitação pendente:</strong> Aguardando aprovação do administrador.
                  <br />
                  <small>Solicitado em: {new Date(solicitacaoPendente.criado_em).toLocaleString('pt-BR')}</small>
                </p>
              </div>
            ) : null}
            
            {!showEmitirForm && !solicitacaoPendente ? (
              <button 
                className="btn btn-primary"
                onClick={() => setShowEmitirForm(true)}
              >
                {isAdmin ? 'Emitir Certificado Digital' : 'Solicitar Certificado Digital'}
              </button>
            ) : null}
            
            {showEmitirForm && (
              <form onSubmit={isAdmin ? handleEmitir : handleSolicitar} className="emitir-form">
                <h3>{isAdmin ? 'Emitir Certificado Digital' : 'Solicitar Certificado Digital'}</h3>
                <p style={{ color: '#6B7280', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                  {isAdmin 
                    ? 'Como administrador, você pode emitir certificados para qualquer usuário do sistema.'
                    : 'Sua solicitação será enviada ao administrador para aprovação.'
                  }
                </p>
                
                <div className="form-group">
                  <label>Validade (anos)</label>
                  <select
                    value={formData.validade_anos}
                    onChange={(e) => setFormData({ ...formData, validade_anos: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 ano</option>
                    <option value={2}>2 anos</option>
                    <option value={3}>3 anos</option>
                    <option value={4}>4 anos</option>
                    <option value={5}>5 anos</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Algoritmo</label>
                  <select
                    value={formData.algoritmo}
                    onChange={(e) => setFormData({ ...formData, algoritmo: e.target.value })}
                  >
                    <option value="RSA">RSA</option>
                    <option value="ECC">ECC (Curva Elíptica)</option>
                  </select>
                </div>

                {formData.algoritmo === 'RSA' && (
                  <div className="form-group">
                    <label>Tamanho da Chave</label>
                    <select
                      value={formData.tamanho_chave}
                      onChange={(e) => setFormData({ ...formData, tamanho_chave: parseInt(e.target.value) })}
                    >
                      <option value={2048}>2048 bits</option>
                      <option value={3072}>3072 bits</option>
                      <option value={4096}>4096 bits</option>
                    </select>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={emitindo}>
                    {emitindo 
                      ? (isAdmin ? 'Emitindo...' : 'Enviando solicitação...')
                      : (isAdmin ? 'Emitir Certificado' : 'Solicitar Certificado')
                    }
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowEmitirForm(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : (
        <div className="certificado-card">
          <div className="certificado-header">
            <div className="certificado-status">
              <span className={`status-badge ${certificado.status}`}>
                {certificado.status === 'ativo' && certificado.valido ? '✅ Válido' : ''}
                {certificado.status === 'expirado' ? '⏰ Expirado' : ''}
                {certificado.status === 'revogado' ? '❌ Revogado' : ''}
                {certificado.status === 'renovado' ? '🔄 Renovado' : ''}
              </span>
            </div>
          </div>

          <div className="certificado-info">
            <div className="info-row">
              <span className="info-label">Número de Série:</span>
              <span className="info-value">{certificado.numero_serie}</span>
            </div>
            <div className="info-row">
              <span className="info-label">UUID:</span>
              <span className="info-value uuid">{certificado.uuid}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Data de Emissão:</span>
              <span className="info-value">
                {new Date(certificado.data_emissao).toLocaleString('pt-BR')}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Data de Expiração:</span>
              <span className="info-value">
                {new Date(certificado.data_expiracao).toLocaleString('pt-BR')}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Algoritmo:</span>
              <span className="info-value">{certificado.algoritmo}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Tamanho da Chave:</span>
              <span className="info-value">{certificado.tamanho_chave} bits</span>
            </div>
          </div>

          <div className="certificado-actions">
            <button className="btn btn-primary" onClick={handleDownload}>
              📥 Download (.pem)
            </button>
            <button className="btn btn-secondary" onClick={handleGerarQRCode}>
              📱 Gerar QR Code
            </button>
            {isAdmin && certificado.status === 'ativo' && (
              <>
                <button className="btn btn-warning" onClick={handleRenovar} disabled={emitindo}>
                  🔄 Renovar
                </button>
                <button className="btn btn-danger" onClick={handleRevogar}>
                  🚫 Revogar
                </button>
              </>
            )}
          </div>

          {qrcode && (
            <div className="qrcode-modal" onClick={() => setQrcode(null)}>
              <div className="qrcode-content" onClick={(e) => e.stopPropagation()}>
                <h3>QR Code do Certificado</h3>
                <img src={qrcode.qrcode} alt="QR Code" />
                <p>Escaneie este código para validar o certificado</p>
                <a href={qrcode.url_validacao} target="_blank" rel="noopener noreferrer">
                  {qrcode.url_validacao}
                </a>
                <button className="btn btn-secondary" onClick={() => setQrcode(null)}>
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Certificados;

